import { parsePassCredential, verifyPassCredential } from '@bre-ticketing/ticket-credentials';
import type { ScanResult } from './api';

/**
 * A season pass at a gate with no network (D-373, docs/scanner.md "Season
 * passes"). The bundle carries tonight's season key (K_s, imported
 * non-extractable) and every pass of that season with its status, version and
 * dates, so the device can decide exactly what the server would, as of the last
 * sync:
 *
 *   authenticity (K_s) → the pass is tonight's season's → known → version →
 *   status → dates → already in tonight? (its ticket admitted, or a local
 *   admission of the pass) → OFFLINE_ACCEPTED.
 *
 * A pass that already has a ticket for tonight (a seat reserved ahead of time,
 * or issued by an earlier online scan) is decided as that ticket. Otherwise the
 * acceptance is queued with the pass id and no ticket, and the server issues
 * the GA comp ticket when the scan syncs.
 *
 * Pure apart from the WebCrypto verify, so it is tested without IndexedDB.
 */

export interface OfflinePass {
  id: string;
  cv: number;
  status: string;
  label: string;
  valid_from: string;
  valid_to: string;
}

export interface PassNight {
  /** The season tonight's game belongs to, when passes cover it. */
  season_id: string;
  /** Tonight's venue date. */
  game_date: string;
  key: CryptoKey;
}

export interface PassTicket {
  tid: string;
  admitted: boolean;
}

export interface OfflinePassDecision {
  result: ScanResult;
  reason_code: string | null;
  reason_text: string | null;
  pass: OfflinePass | null;
  presented_cv: number | null;
  /** When the pass already has tonight's ticket: admit that ticket. */
  ticket_id: string | null;
}

/** Null when the string is not a season pass credential. */
export async function decidePassOffline(
  raw: string,
  night: PassNight | null,
  lookup: { pass: (id: string) => Promise<OfflinePass | undefined>; ticketFor: (passId: string) => Promise<PassTicket | undefined>; admittedLocally: (passId: string) => Promise<boolean> },
): Promise<OfflinePassDecision | null> {
  const parsed = parsePassCredential(raw);
  if (!parsed.ok) return null;
  const payload = parsed.payload;
  const refuse = (result: ScanResult, reasonCode: string, text: string, pass: OfflinePass | null = null): OfflinePassDecision => ({ result, reason_code: reasonCode, reason_text: text, pass, presented_cv: payload.cv, ticket_id: null });
  if (!night) return refuse('WRONG_EVENT', 'PASS_WRONG_EVENT', 'Season passes do not cover tonight, or this device has no pass list for it');
  if (payload.sid !== night.season_id) return refuse('WRONG_EVENT', 'PASS_WRONG_EVENT', 'This pass is for another season');
  if (!(await verifyPassCredential(night.key, parsed))) return refuse('INVALID', 'INVALID', 'The pass is not genuine');
  const pass = await lookup.pass(payload.sp);
  if (!pass) return refuse('INVALID', 'INVALID', 'No such season pass in the bundle');
  if (payload.cv < pass.cv) return refuse('TRANSFERRED', 'PASS_REISSUED', 'Superseded: this pass was reissued. Ask the holder for their new pass.', pass);
  if (payload.cv > pass.cv) return refuse('INVALID', 'INVALID', 'No such season pass version', pass);
  if (pass.status === 'SUSPENDED') return refuse('REVOKED', 'PASS_SUSPENDED', 'This pass is suspended. Send the holder to the box office.', pass);
  if (pass.status !== 'ACTIVE') return refuse('REVOKED', 'PASS_REVOKED', 'This pass has been revoked. Send the holder to the box office.', pass);
  if (night.game_date < pass.valid_from) return refuse('NOT_YET_VALID', 'PASS_NOT_YET_VALID', `This pass is valid from ${pass.valid_from}`, pass);
  if (night.game_date > pass.valid_to) return refuse('EXPIRED', 'PASS_EXPIRED', `This pass expired on ${pass.valid_to}`, pass);
  const ticket = await lookup.ticketFor(pass.id);
  if (ticket?.admitted || (!ticket && (await lookup.admittedLocally(pass.id)))) {
    return { result: 'ALREADY_SCANNED', reason_code: 'DUPLICATE', reason_text: 'This pass is already in tonight', pass, presented_cv: payload.cv, ticket_id: ticket?.tid ?? null };
  }
  return { result: 'OFFLINE_ACCEPTED', reason_code: null, reason_text: null, pass, presented_cv: payload.cv, ticket_id: ticket?.tid ?? null };
}
